import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Polyline } from 'react-leaflet';
import { FaMapMarkerAlt, FaTruck } from 'react-icons/fa';
import { HiCheckBadge } from 'react-icons/hi2';
import { listPublishedRequests } from '../../../services/requestsApi';
import { SERVICE_TYPE_LABELS } from '../../../utils/feedItems';
import { resolveLocationLabel } from '../../../services/unlocodeService';
import { subscribeSessionUser, fetchSessionUser } from '../../../services/sessionUser';
import '../../home/sections/css/Trends.css';
import './RequestsHighlights.css';

const FEATURED_REQUEST_TARGET = 6;
const REQUEST_FETCH_LIMIT = 24;
const DESKTOP_VISIBLE_CARDS = 4;
const CAROUSEL_TRANSITION_MS = 600;
const AUTOPLAY_INTERVAL_MS = 5200;
const DEFAULT_MAP_CENTER = [23.6345, -102.5528];
const DEFAULT_MAP_ZOOM = 5;
const MAP_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const MAP_TILE_ATTRIBUTION = '&copy; OpenStreetMap contributors';
const LOGIN_REDIRECT_STORAGE_KEY = 'flotte:pendingProtectedRedirect';

const getVisibleCardCount = () => {
  if (typeof window === 'undefined') {
    return DESKTOP_VISIBLE_CARDS;
  }
  const width = window.innerWidth;
  if (width >= 1280) return 4;
  if (width >= 1024) return 3;
  if (width >= 640) return 2;
  return 1;
};

const formatBudgetLabel = (value, currency) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const normalizedCurrency =
    typeof currency === 'string' && currency.trim().length === 3
      ? currency.trim().toUpperCase()
      : 'MXN';
  const numeric = Number(String(value).replace(/[^\d.-]/g, ''));
  if (Number.isFinite(numeric)) {
    const locale = normalizedCurrency === 'USD' ? 'en-US' : 'es-MX';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 0,
    }).format(Math.max(0, numeric));
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return String(value);
};

const formatDateLabel = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const isCoordinateValid = coord =>
  coord &&
  Number.isFinite(coord.latitude) &&
  Number.isFinite(coord.longitude);

const getMapView = (pickupCoordinates, dropoffCoordinates) => {
  const hasPickup = isCoordinateValid(pickupCoordinates);
  const hasDropoff = isCoordinateValid(dropoffCoordinates);

  if (hasPickup && hasDropoff) {
    const centerLat = (pickupCoordinates.latitude + dropoffCoordinates.latitude) / 2;
    const centerLng = (pickupCoordinates.longitude + dropoffCoordinates.longitude) / 2;
    const latDiff = Math.abs(pickupCoordinates.latitude - dropoffCoordinates.latitude);
    const lngDiff = Math.abs(pickupCoordinates.longitude - dropoffCoordinates.longitude);
    const maxDiff = Math.max(latDiff, lngDiff);
    let zoom = 7;
    if (maxDiff > 20) zoom = 4;
    else if (maxDiff > 10) zoom = 5;
    else if (maxDiff > 5) zoom = 6;
    return {
      center: [centerLat, centerLng],
      zoom,
    };
  }

  if (hasPickup) {
    return {
      center: [pickupCoordinates.latitude, pickupCoordinates.longitude],
      zoom: 7,
    };
  }

  if (hasDropoff) {
    return {
      center: [dropoffCoordinates.latitude, dropoffCoordinates.longitude],
      zoom: 7,
    };
  }

  return {
    center: DEFAULT_MAP_CENTER,
    zoom: DEFAULT_MAP_ZOOM,
  };
};

const rememberLoginRedirect = targetPath => {
  if (typeof window === 'undefined' || !targetPath) return;
  try {
    window.sessionStorage.setItem(
      LOGIN_REDIRECT_STORAGE_KEY,
      JSON.stringify({
        path: targetPath,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    console.warn('No se pudo guardar la redireccion pendiente.', error);
  }
};

function RequestMiniMap({ pickupCoordinates, dropoffCoordinates }) {
  const hasPickup = isCoordinateValid(pickupCoordinates);
  const hasDropoff = isCoordinateValid(dropoffCoordinates);
  const mapView = useMemo(
    () => getMapView(pickupCoordinates, dropoffCoordinates),
    [pickupCoordinates, dropoffCoordinates]
  );

  if (!hasPickup && !hasDropoff) {
    return (
      <div className="featured-request-card__map featured-request-card__map--empty">
        <FaMapMarkerAlt aria-hidden="true" />
        <span>Ubicacion pendiente</span>
      </div>
    );
  }

  return (
    <div className="featured-request-card__map">
      <MapContainer
        center={mapView.center}
        zoom={mapView.zoom}
        minZoom={3}
        maxZoom={12}
        zoomControl={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        dragging={false}
        keyboard={false}
        touchZoom={false}
        attributionControl={false}
        className="featured-request-card__leaflet"
      >
        <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} />
        {hasPickup ? (
          <CircleMarker
            center={[pickupCoordinates.latitude, pickupCoordinates.longitude]}
            radius={8}
            pathOptions={{
              color: '#2563eb',
              fillColor: '#2563eb',
              fillOpacity: 0.6,
              weight: 2,
            }}
          />
        ) : null}
        {hasDropoff ? (
          <CircleMarker
            center={[dropoffCoordinates.latitude, dropoffCoordinates.longitude]}
            radius={8}
            pathOptions={{
              color: '#f97316',
              fillColor: '#f97316',
              fillOpacity: 0.6,
              weight: 2,
            }}
          />
        ) : null}
        {hasPickup && hasDropoff ? (
          <Polyline
            positions={[
              [pickupCoordinates.latitude, pickupCoordinates.longitude],
              [dropoffCoordinates.latitude, dropoffCoordinates.longitude],
            ]}
            pathOptions={{
              color: '#0f172a',
              weight: 3,
              opacity: 0.45,
            }}
          />
        ) : null}
      </MapContainer>
    </div>
  );
}

function RequestHighlightCard({ request, isAuthenticated, onViewRequest }) {
  const hasDropoff = Boolean(request.dropoffLocation);
  const hasPickup = Boolean(request.pickupLocation);
  const hasMap = isCoordinateValid(request.pickupCoordinates) || isCoordinateValid(request.dropoffCoordinates);
  const mediaClassName = `listing-card__media${hasMap ? '' : ' listing-card__media--empty'}`;
  const companyNode = isAuthenticated && request.companyName ? (
    request.companySlug ? (
      <Link to={`/empresa/${request.companySlug}`} className="listing-card__provider">
        <FaTruck aria-hidden="true" />
        {request.companyName}
      </Link>
    ) : (
      <span className="listing-card__provider">
        <FaTruck aria-hidden="true" />
        {request.companyName}
      </span>
    )
  ) : null;

  return (
    <article className="listing-card featured-request-card" role="listitem">
      <div className={mediaClassName}>
        <RequestMiniMap
          pickupCoordinates={request.pickupCoordinates}
          dropoffCoordinates={request.dropoffCoordinates}
        />
        <span className="listing-card__badge listing-card__badge--verified" aria-hidden="true">
          <HiCheckBadge aria-hidden="true" />
        </span>
        <span className="listing-card__rating listing-card__rating--verified">
          <strong>{request.serviceTypeLabel}</strong>
          <span>Demanda activa</span>
        </span>
      </div>

      <div className="listing-card__body">
        <div className="listing-card__content">
          <h3 title={request.title}>{request.title}</h3>
          {hasPickup ? (
            <p className="listing-card__location">
              <FaMapMarkerAlt aria-hidden="true" />
              Origen: {request.pickupLocation}
            </p>
          ) : null}
          {hasDropoff ? (
            <p className="listing-card__location listing-card__location--secondary">
              <FaMapMarkerAlt aria-hidden="true" />
              Destino: {request.dropoffLocation}
            </p>
          ) : null}
          {companyNode}
          {request.neededByLabel ? (
            <div className="listing-card__meta">
              <span>Necesario antes de {request.neededByLabel}</span>
            </div>
          ) : null}
        </div>

        <div className="listing-card__footer">
          <div className="listing-card__price">
            <span>Presupuesto</span>
            <strong>{request.budgetLabel || 'A convenir'}</strong>
          </div>
          <button
            type="button"
            className="listing-card__cta"
            onClick={() => onViewRequest?.(request.href)}
          >
            Ver solicitud
          </button>
        </div>
      </div>
    </article>
  );
}

function RequestHighlightSkeleton() {
  return (
    <article className="listing-card featured-request-card featured-request-card--skeleton" aria-hidden="true">
      <div className="listing-card__media">
        <span className="featured-request-card__skeleton featured-request-card__skeleton--map" />
      </div>
      <div className="listing-card__body">
        <div className="listing-card__content">
          <span className="featured-request-card__skeleton featured-request-card__skeleton--pill" />
          <span className="featured-request-card__skeleton featured-request-card__skeleton--title" />
          <span className="featured-request-card__skeleton featured-request-card__skeleton--line" />
          <span className="featured-request-card__skeleton featured-request-card__skeleton--line short" />
        </div>
        <div className="listing-card__footer">
          <span className="featured-request-card__skeleton featured-request-card__skeleton--price" />
          <span className="featured-request-card__skeleton featured-request-card__skeleton--cta" />
        </div>
      </div>
    </article>
  );
}

export default function RequestsHighlights() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [visibleCards, setVisibleCards] = useState(() => getVisibleCardCount());
  const [activeSlide, setActiveSlide] = useState(0);
  const [slideOffset, setSlideOffset] = useState(0);
  const [isTrackAnimated, setIsTrackAnimated] = useState(true);
  const trackRef = useRef(null);
  const cardRef = useRef(null);
  const [resolvedLocations, setResolvedLocations] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const fetchRequests = async () => {
      setLoading(true);
      setError('');
      try {
        const items = await listPublishedRequests({ limit: REQUEST_FETCH_LIMIT });
        if (!cancelled) {
          setRequests(Array.isArray(items) ? items : []);
        }
      } catch (err) {
        console.warn('No se pudieron cargar solicitudes destacadas.', err);
        if (!cancelled) {
          setError('No pudimos cargar las solicitudes destacadas en este momento.');
          setRequests([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchRequests();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    fetchSessionUser()
      .then(profile => {
        if (isMounted) {
          setIsAuthenticated(Boolean(profile?.id));
        }
      })
      .catch(() => {
        if (isMounted) {
          setIsAuthenticated(false);
        }
      });

    const unsubscribe = subscribeSessionUser(profile => {
      setIsAuthenticated(Boolean(profile?.id));
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const labelsToResolve = new Set();
    const registerLabel = label => {
      const normalized = typeof label === 'string' ? label.trim() : '';
      if (!normalized) {
        return;
      }
      if (Object.prototype.hasOwnProperty.call(resolvedLocations, normalized)) {
        return;
      }
      labelsToResolve.add(normalized);
    };

    requests.forEach(item => {
      if (!item) return;
      if (!isCoordinateValid(item.pickupCoordinates) && item.pickupLocation) {
        registerLabel(item.pickupLocation);
      }
      if (!isCoordinateValid(item.dropoffCoordinates) && item.dropoffLocation) {
        registerLabel(item.dropoffLocation);
      }
    });

    if (!labelsToResolve.size) {
      return undefined;
    }

    const resolveMissingLocations = async () => {
      const updates = {};
      for (const label of labelsToResolve) {
        try {
          const entry = await resolveLocationLabel(label);
          if (cancelled) {
            return;
          }
          if (entry && Number.isFinite(entry.latitude) && Number.isFinite(entry.longitude)) {
            updates[label] = {
              latitude: entry.latitude,
              longitude: entry.longitude,
            };
          } else {
            updates[label] = null;
          }
        } catch (locationError) {
          if (cancelled) {
            return;
          }
          console.warn('No se pudo georreferenciar la ubicacion destacada:', label, locationError);
          updates[label] = null;
        }
      }
      if (!cancelled && Object.keys(updates).length) {
        setResolvedLocations(prev => ({ ...prev, ...updates }));
      }
    };

    resolveMissingLocations();

    return () => {
      cancelled = true;
    };
  }, [requests, resolvedLocations]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateVisibleCards = () => {
      setVisibleCards(getVisibleCardCount());
    };

    updateVisibleCards();
    window.addEventListener('resize', updateVisibleCards);
    return () => window.removeEventListener('resize', updateVisibleCards);
  }, []);

  const requestCards = useMemo(() => {
    if (!Array.isArray(requests)) {
      return [];
    }
    return requests.slice(0, FEATURED_REQUEST_TARGET).map(item => {
      const pickupKey = typeof item.pickupLocation === 'string' ? item.pickupLocation.trim() : '';
      const dropoffKey =
        typeof item.dropoffLocation === 'string' ? item.dropoffLocation.trim() : '';
      const pickupCoordinates =
        item.pickupCoordinates ||
        (pickupKey ? resolvedLocations[pickupKey] || null : null);
      const dropoffCoordinates =
        item.dropoffCoordinates ||
        (dropoffKey ? resolvedLocations[dropoffKey] || null : null);

      return {
        id: item.id,
        title: item.title || 'Solicitud sin titulo',
        pickupLocation: item.pickupLocation || 'Ubicacion por confirmar',
        dropoffLocation: item.dropoffLocation || '',
        pickupCoordinates,
        dropoffCoordinates,
        neededByLabel: formatDateLabel(item.neededBy),
        budgetLabel: formatBudgetLabel(item.budget, item.currency),
        companyName: item.company?.name || '',
        companySlug: item.company?.slug || '',
        serviceTypeLabel: SERVICE_TYPE_LABELS[item.serviceType] || 'Servicio',
        href: item.id ? `/solicitudes/${item.id}` : '/solicitudes',
      };
    });
  }, [requests, resolvedLocations]);

  useEffect(() => {
    setActiveSlide(0);
  }, [requestCards.length]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const measureSlideOffset = () => {
      if (!cardRef.current) {
        setSlideOffset(0);
        return;
      }
      const cardWidth = cardRef.current.getBoundingClientRect().width;
      let gap = 0;
      if (trackRef.current) {
        const styles = window.getComputedStyle(trackRef.current);
        const parsedGap =
          parseFloat(styles.columnGap || styles.gap || styles.rowGap || '0') || 0;
        gap = Number.isNaN(parsedGap) ? 0 : parsedGap;
      }
      setSlideOffset(cardWidth + gap);
    };
    measureSlideOffset();
    window.addEventListener('resize', measureSlideOffset);
    return () => window.removeEventListener('resize', measureSlideOffset);
  }, [visibleCards, requestCards.length]);

  const slideCount = requestCards.length;
  const shouldAutoplay = slideCount > visibleCards;
  const slidesToRender = useMemo(() => {
    if (!slideCount) return [];
    if (!shouldAutoplay) return requestCards;
    const clones = requestCards.slice(0, Math.min(visibleCards, slideCount));
    return [...requestCards, ...clones];
  }, [requestCards, shouldAutoplay, visibleCards, slideCount]);
  const placeholderCount = Math.max(0, visibleCards - slideCount);

  useEffect(() => {
    if (!shouldAutoplay) {
      setActiveSlide(0);
      return;
    }
    if (!slideOffset) {
      return;
    }
    const interval = setInterval(() => {
      setActiveSlide(previous => previous + 1);
    }, AUTOPLAY_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [shouldAutoplay, slideOffset]);

  useEffect(() => {
    if (!shouldAutoplay || !slideCount) {
      setIsTrackAnimated(true);
      return;
    }
    if (activeSlide === slideCount) {
      const timeout = setTimeout(() => {
        setIsTrackAnimated(false);
        setActiveSlide(0);
      }, CAROUSEL_TRANSITION_MS);
      return () => clearTimeout(timeout);
    }
  }, [activeSlide, shouldAutoplay, slideCount]);

  useEffect(() => {
    if (isTrackAnimated) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      setIsTrackAnimated(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isTrackAnimated]);

  let hasAttachedRef = false;
  const attachFirstSlideRef = () => {
    if (hasAttachedRef) return null;
    hasAttachedRef = true;
    return cardRef;
  };

  const handleViewRequest = useCallback(
    href => {
      if (!href) return;
      if (isAuthenticated) {
        navigate(href);
        return;
      }
      rememberLoginRedirect(href);
      navigate('/login');
    },
    [isAuthenticated, navigate]
  );

  const showEmptyMessage = !loading && !slideCount && !error;

  return (
    <section className="featured-opportunities requests-highlights" aria-labelledby="requests-highlights-title">
      <div className="featured-opportunities__inner">
        <header className="featured-opportunities__header">
          <div className="featured-opportunities__titles">
            <p className="featured-opportunities__eyebrow">Demandas verificadas</p>
            <h2 id="requests-highlights-title">Solicitudes destacadas</h2>
            <p className="featured-opportunities__description">
              Clientes activos buscan cobertura en estos corredores. Analiza el mapa integrado en cada tarjeta y propón tu oferta.
            </p>
          </div>
          <Link className="featured-opportunities__link" to="/marketplace?segment=requests">
            Ver todas las solicitudes →
          </Link>
        </header>

        {error ? (
          <p className="requests-highlights__status requests-highlights__status--error">{error}</p>
        ) : null}

        {loading && !slideCount ? (
          <div className="featured-carousel featured-carousel--loading">
            <div className="featured-carousel__viewport" role="list">
              <div className="featured-carousel__track featured-carousel__track--static">
                {Array.from({ length: visibleCards || 1 }).map((_, index) => (
                  <div className="featured-carousel__slide" role="listitem" key={`request-placeholder-${index}`}>
                    <RequestHighlightSkeleton />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="featured-carousel">
            <div className="featured-carousel__viewport">
              <div
                className={`featured-carousel__track${
                  isTrackAnimated ? '' : ' featured-carousel__track--static'
                }`}
                role="list"
                ref={trackRef}
                style={{
                  transform: `translate3d(-${activeSlide * slideOffset}px, 0, 0)`,
                  '--featured-carousel-visible': visibleCards,
                }}
              >
                {slidesToRender.map((request, index) => (
                  <div
                    className="featured-carousel__slide"
                    role="listitem"
                    key={`${request.id || 'request'}-${index}`}
                    ref={attachFirstSlideRef()}
                  >
                    <RequestHighlightCard
                      request={request}
                      isAuthenticated={isAuthenticated}
                      onViewRequest={handleViewRequest}
                    />
                  </div>
                ))}
                {placeholderCount > 0 &&
                  Array.from({ length: placeholderCount }).map((_, index) => (
                    <div
                      className="featured-carousel__slide"
                      role="listitem"
                      key={`requests-fallback-${index}`}
                      ref={attachFirstSlideRef()}
                    >
                      <RequestHighlightSkeleton />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {showEmptyMessage ? (
          <p className="featured-opportunities__empty">
            No hay solicitudes publicadas por ahora. Vuelve más tarde para descubrir nuevas oportunidades.
          </p>
        ) : null}
      </div>
    </section>
  );
}
